import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Phone, MessageSquare, Send, Zap } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuickQuoteModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    product: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/quote-request`, {
        name: formData.name,
        email: 'quickquote@placeholder.com', // Placeholder
        phone: formData.phone,
        company: 'Quick Quote',
        product_interest: formData.product,
        quantity: 'N/A',
        delivery_timeline: 'ASAP',
        additional_requirements: formData.message || 'Quick quote request'
      });
      
      toast.success('Quote request sent! We\'ll get back to you soon.');
      setFormData({ name: '', phone: '', product: '', message: '' });
      onClose();
    } catch (error) {
      toast.error('Error sending request. Please try calling us directly.');
    }
    
    setLoading(false);
  };

  const handleWhatsAppQuote = () => {
    const message = `Hi! I need a quote for: ${formData.product || 'copper products'}. Name: ${formData.name}, Phone: ${formData.phone}`;
    window.open(`https://wa.me/919966005098?text=${encodeURIComponent(message)}`, '_blank');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, y: 50 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 50 }}
          transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          className="bg-white rounded-3xl max-w-lg w-full p-8 shadow-2xl relative"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition"
          >
            <X size={20} />
          </button>

          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-500 rounded-2xl flex items-center justify-center text-white">
                <Zap size={24} />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Quick Quote</h2>
                <p className="text-sm text-orange-500 font-semibold">Fast Response ⚡</p>
              </div>
            </div>
            <p className="text-gray-600">
              Fill this simple form and we'll get back to you immediately.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Your Name *</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                placeholder="John Doe"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Phone Number *</label>
              <input
                type="tel"
                required
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                placeholder="+91 98765 43210"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Product Needed *</label>
              <select
                required
                value={formData.product}
                onChange={(e) => setFormData({ ...formData, product: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent"
              >
                <option value="">Select Product</option>
                <option value="Copper Flats">Copper Flats</option>
                <option value="Copper Rods & Wires">Copper Rods & Wires</option>
                <option value="Copper Strips">Copper Strips</option>
                <option value="Copper Sheets">Copper Sheets</option>
                <option value="Copper Coils">Copper Coils</option>
                <option value="Copper Pipes">Copper Pipes</option>
                <option value="Copper Ingots">Copper Ingots</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Message (Optional)</label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                rows="2"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                placeholder="Quantity, specifications..."
              />
            </div>

            {/* Action Buttons */}
            <div className="space-y-3 pt-2">
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-orange-400 to-orange-500 text-white px-6 py-4 rounded-lg font-bold hover:shadow-lg transition disabled:opacity-50 flex items-center justify-center"
              >
                {loading ? 'Sending...' : 'Submit Quote Request'}
                <Send size={18} className="ml-2" />
              </button>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => window.location.href = 'tel:+919966005098'}
                  className="flex-1 bg-blue-900 text-white px-4 py-3 rounded-lg font-semibold hover:bg-blue-800 transition flex items-center justify-center"
                >
                  <Phone size={18} className="mr-2" />
                  Call Now
                </button>
                <button
                  type="button"
                  onClick={handleWhatsAppQuote}
                  className="flex-1 bg-green-600 text-white px-4 py-3 rounded-lg font-semibold hover:bg-green-700 transition flex items-center justify-center"
                >
                  <MessageSquare size={18} className="mr-2" />
                  WhatsApp
                </button>
              </div>
            </div>
          </form>

          {/* Trust Badge */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
              <Zap size={16} className="text-orange-500" />
              <span className="font-semibold">Guaranteed quick response</span>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default QuickQuoteModal;
